/**
 * StatementCard — collapsible per-statement detail inside the drawer.
 *
 * The header shows the statement, its score and response count. Clicking
 * expands the 7-point Likert (or NPS) distribution. We auto-detect the scale
 * from the distribution array length so both question types render cleanly.
 */

import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BAND_PALETTE, bandFromScore, formatBipolarScore } from '../config/bandColours';
import type { StatementDetail } from '../types';

export interface StatementCardProps {
  statement: StatementDetail;
}

function describeBuckets(length: number): {
  /** Indices to render (skips index 0 for Likert keyed 1..7). */
  indices: number[];
  /** Label resolver per index. */
  label: (idx: number) => string;
} {
  // NPS: 0..10 (length 11)
  if (length === 11) {
    return {
      indices: Array.from({ length: 11 }, (_, i) => i),
      label: (idx) => String(idx),
    };
  }
  // Likert 1..7 (max key 7 → array length 8, index 0 empty)
  if (length === 8) {
    return {
      indices: [1, 2, 3, 4, 5, 6, 7],
      label: (idx) => {
        if (idx === 1) return 'Strongly disagree';
        if (idx === 4) return 'Neutral';
        if (idx === 7) return 'Strongly agree';
        return '';
      },
    };
  }
  // Likert 0..6 (length 7)
  if (length === 7) {
    return {
      indices: [0, 1, 2, 3, 4, 5, 6],
      label: (idx) => {
        if (idx === 0) return 'Strongly disagree';
        if (idx === 3) return 'Neutral';
        if (idx === 6) return 'Strongly agree';
        return '';
      },
    };
  }
  // Fallback: numeric labels for every index.
  return {
    indices: Array.from({ length }, (_, i) => i),
    label: (idx) => String(idx),
  };
}

export const StatementCard: React.FunctionComponent<StatementCardProps> = ({
  statement,
}) => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const band = bandFromScore(statement.score);
  const palette = BAND_PALETTE[band];
  const { indices, label } = describeBuckets(statement.distribution.length);
  const max = Math.max(0, ...indices.map((i) => statement.distribution[i] ?? 0));

  return (
    <div
      style={{
        marginTop: 12,
        border: '1px solid #E5E7EB',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: '#FAFBFC',
          border: 'none',
          padding: '13px 15px',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0F172A' }}>
            “{statement.text}”
          </span>
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: palette.fg,
              whiteSpace: 'nowrap',
            }}
          >
            {formatBipolarScore(statement.score)}
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 4 }}>
          <FormattedMessage
            id="marketerView.drawer.statementResponses"
            defaultMessage="{n, plural, one {# response} other {# responses}} · click to {open, select, true {hide} other {see}} distribution"
            values={{ n: statement.responseCount, open: open ? 'true' : 'false' }}
          />
        </div>
      </button>
      {open && (
        <div style={{ padding: '14px 15px' }}>
          {indices.map((idx) => {
            const count = statement.distribution[idx] ?? 0;
            const pct = statement.responseCount
              ? Math.round((count / statement.responseCount) * 100)
              : 0;
            const labelText = label(idx);
            return (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '88px 1fr 56px',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 10.5,
                    color: '#94A3B8',
                    textAlign: 'right',
                    fontWeight: 400,
                  }}
                  aria-label={intl.formatMessage(
                    { id: 'marketerView.drawer.bucketLabel', defaultMessage: 'Bucket {idx}' },
                    { idx },
                  )}
                >
                  {labelText || idx}
                </span>
                <div
                  style={{
                    height: 14,
                    background: '#F0F2F6',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${max ? (count / max) * 100 : 0}%`,
                      background: palette.fg,
                      opacity: 0.85,
                      borderRadius: 4,
                    }}
                  />
                </div>
                <span style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>
                  {count} ({pct}%)
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
